import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import { Badge } from '../ui/badge';
import { LogOut, ArrowRight, RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import type { DropoffDeepDiveData } from '../../types/journeys';

interface DropoffDrawerProps {
  open: boolean;
  onClose: () => void;
  loading: boolean;
  data: DropoffDeepDiveData | null;
}

export default function DropoffDrawer({
  open,
  onClose,
  loading,
  data,
}: DropoffDrawerProps) {
  // Color coding for drop-off rate severity
  const getRateColor = (rate: number) => {
    if (rate >= 70) return 'text-red-600 bg-red-50 border-red-200';
    if (rate >= 50) return 'text-orange-600 bg-orange-50 border-orange-200';
    if (rate >= 30) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-yellow-600 bg-yellow-50 border-yellow-200';
  };

  const getRateProgressColor = (rate: number) => {
    if (rate >= 70) return 'bg-red-500';
    if (rate >= 50) return 'bg-orange-500';
    if (rate >= 30) return 'bg-amber-500';
    return 'bg-yellow-500';
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <LogOut className="h-5 w-5 text-red-500" />
            Drop-off Analysis
          </SheetTitle>
          {data && (
            <SheetDescription className="text-base text-gray-900 font-medium">
              {data.cluster.label}
            </SheetDescription>
          )}
        </SheetHeader>

        <div className="mt-6 space-y-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : data ? (
            <>
              {/* Stats Overview */}
              <section>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  Overview
                </h3>
                <div className="bg-gray-50 rounded-xl p-4 border">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-600">Drop-off Rate</span>
                    <Badge className={`text-sm px-3 py-1 font-semibold ${getRateColor(data.dropoff_stats.dropoff_rate)}`}>
                      {data.dropoff_stats.dropoff_rate}%
                    </Badge>
                  </div>
                  <div className="relative h-2 w-full overflow-hidden rounded-full bg-gray-200 mb-4">
                    <div 
                      className={`h-full transition-all duration-500 ease-out ${getRateProgressColor(data.dropoff_stats.dropoff_rate)}`}
                      style={{ width: `${data.dropoff_stats.dropoff_rate}%` }}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">
                        {data.dropoff_stats.total_visits.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">Total Visits</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-600">
                        {data.dropoff_stats.dropoff_count.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">Dropped Off</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600">
                        {data.dropoff_stats.continued_count.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-500">Continued</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Incoming Paths */}
              {data.incoming_paths.length > 0 && (
                <section>
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                    How Users Got Here Before Dropping Off
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    These are the topics users visited right before arriving at this drop-off point:
                  </p>
                  <div className="space-y-2">
                    {data.incoming_paths.map((path, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border hover:border-gray-300 transition-colors"
                        style={{
                          opacity: 0,
                          animation: `topicCardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${idx * 50}ms forwards`,
                        }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-medium text-gray-900 truncate">
                              {path.previous_topic.label}
                            </span>
                            <ArrowRight className="h-4 w-4 text-gray-400 shrink-0" />
                            <span className="text-red-600 font-medium shrink-0">Drop-off</span>
                          </div>
                        </div>
                        <Badge variant="secondary" className="shrink-0">
                          {path.percentage}% · {path.session_count} sessions
                        </Badge>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Side-by-side comparison */}
              <section>
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
                  What Were They Asking?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Dropped off examples */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-red-700">
                      <AlertTriangle className="h-4 w-4" />
                      Users Who Dropped Off
                    </div>
                    {data.dropoff_examples.length > 0 ? (
                      <div className="space-y-2">
                        {data.dropoff_examples.slice(0, 5).map((example, idx) => (
                          <div
                            key={idx}
                            className="p-3 bg-red-50 rounded-lg border border-red-100 text-sm text-gray-700"
                            style={{
                              opacity: 0,
                              animation: `topicCardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${100 + idx * 50}ms forwards`,
                            }}
                          >
                            "{example}"
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No examples available</p>
                    )}
                  </div>

                  {/* Continued examples */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                      <CheckCircle className="h-4 w-4" />
                      Users Who Continued
                    </div>
                    {data.continued_examples.length > 0 ? (
                      <div className="space-y-2">
                        {data.continued_examples.slice(0, 5).map((example, idx) => (
                          <div
                            key={idx}
                            className="p-3 bg-green-50 rounded-lg border border-green-100 text-sm text-gray-700"
                            style={{
                              opacity: 0,
                              animation: `topicCardFadeIn 400ms cubic-bezier(0.16, 1, 0.3, 1) ${100 + idx * 50}ms forwards`,
                            }}
                          >
                            "{example}"
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No examples available</p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  💡 Compare these examples to spot patterns. Are drop-off users asking more complex questions? 
                  Are they expressing frustration? Understanding the difference can reveal actionable insights.
                </p>
              </section>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <LogOut className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No data available</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

